<template>
  <section class="wrapper-reports">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.ReportsScreen.REPORTS }}
      </h1>
    </div>
    <div class="custom-box">
      <h1 class="title is-size-6 has-text-color-7">
        {{ App.ReportsScreen.REPORTS_CONFIGURATION }}
      </h1>
      <div class="has-text-weight-medium has-text-color-7">
        <span>{{ App.ReportsScreen.REPORT_TYPE }}</span>
      </div>
      <div class="custom-width-250">
        <b-select
          v-model="reportType"
          class="spacing-mb-15"
          :placeholder="General.SELECT_TYPE"
          expanded
        >
          <option
            v-for="(option, index) in getReportTypes"
            :key="index"
            :value="option"
          >
            {{ option.type }}
          </option>
        </b-select>
      </div>
      <div class="title is-flex is-size-6 has-text-color-7">
        {{ App.ReportsScreen.FILTERS }}
        <div class="line-label" />
      </div>
      <div id="subject_field" class="my-10">
        <div class="field-box">
          <div class="has-text-weight-medium has-text-color-7 spacing-mr-5">
            <span>{{ General.SUBJECT }}</span>
          </div>
          <div class="optional-label has-text-weight-bold">
            {{ General.OPTIONAL }}
          </div>
        </div>
        <b-autocomplete
          v-model="subjectName"
          aria-role="subject"
          :data="subjects"
          autocomplete
          field="completeName"
          name="subject"
          :placeholder="General.PLACEHOLDER.SUBJECT"
          keepFirst
          @typing="getSubjects"
          @select="option => (reportSubject = option)"
        >
          <template slot-scope="props">
            <div>
              {{ props.option.completeName }}
            </div>
          </template>
          <template slot="empty">
            <p v-if="!isLoading">
              {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </template>
        </b-autocomplete>
      </div>

      <div id="bookmark_field" class="my-10">
        <div class="field-box">
          <div class="has-text-weight-medium has-text-color-7 spacing-mr-5">
            <span>{{ General.BOOKMARKERS }}</span>
          </div>
          <div class="optional-label has-text-weight-bold">
            {{ General.OPTIONAL }}
          </div>
        </div>
        <CustomTagInput
          v-model="reportBookmarks"
          :data="getTags()"
          autocomplete
          field="name"
          name="bookmarkers"
          :placeholder="General.PLACEHOLDER.SEARCH_BOOKMARKER"
          rounded
          :hasCounter="false"
          @typing="setSearchTag"
        >
          <template slot-scope="props">
            <span>{{ props.option.name }}</span>
          </template>
          <template slot="empty">
            <p v-if="!isLoading">
              {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </template>
        </CustomTagInput>
      </div>

      <div id="capacity_field" class="my-10">
        <div class="field-box">
          <div class="has-text-weight-medium has-text-color-7 spacing-mr-5">
            <span>{{ General.UNITY }}</span>
          </div>
          <div class="optional-label has-text-weight-bold">
            {{ General.OPTIONAL }}
          </div>
        </div>
        <CustomTagInput
          v-model="reportCapacities"
          :data="capacitiesFiltered(filterCapacity)"
          autocomplete
          field="showCaseName"
          name="capacity"
          :placeholder="General.PLACEHOLDER.SEARCH_CAPACITY"
          rounded
          :hasCounter="false"
          @typing="setFilterCapacity"
        >
          <template slot-scope="props">
            <span>{{ props.option.name }}</span>
          </template>
          <template slot="empty">
            <p v-if="!isLoading">
              {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </template>
        </CustomTagInput>
      </div>

      <div id="part_field" class="my-10">
        <div class="field-box">
          <div class="has-text-weight-medium has-text-color-7 spacing-mr-5">
            <span>{{ General.ORGAN }}</span>
          </div>
          <div class="optional-label has-text-weight-bold">
            {{ General.OPTIONAL }}
          </div>
        </div>
        <CustomTagInput
          v-model="reportPart"
          :data="getFilteredParts(filterPart)"
          autocomplete
          field="abbreviation"
          name="part"
          :placeholder="General.PLACEHOLDER.SEARCH_ORGAN"
          rounded
          :hasCounter="false"
          @typing="setFilterPart"
        >
          <template slot-scope="props">
            <span>{{ props.option.name }}</span>
          </template>
          <template slot="empty">
            <p v-if="!isLoading">
              {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </template>
        </CustomTagInput>
      </div>

      <div id="date_field" class="my-10">
        <div class="field-box">
          <div class="has-text-weight-medium has-text-color-7 spacing-mr-5">
            <span>{{ General.PERIOD }}</span>
          </div>
          <div class="optional-label has-text-weight-bold">
            {{ General.OPTIONAL }}
          </div>
        </div>
        <section class="is-flex is-align-items-flex-end">
          <b-datepicker
            v-model="date.start"
            :placeholder="General.PLACEHOLDER.SELECT_PERIOD_START"
            icon="calendar-today"
            locale="pt-BR"
            position="is-top-right"
            class="custom-datepicker custom-width-300"
            editable
            @input="resetDateEnd"
          />
          <span class="mx-10">{{ General.UP_UNTIL.toLowerCase() }}</span>
          <b-datepicker
            :key="dataEndKey"
            v-model="date.end"
            :disabled="isInvalidDateStart"
            :minDate="date.start"
            :placeholder="General.PLACEHOLDER.SELECT_PERIOD_END"
            icon="calendar-today"
            locale="pt-BR"
            position="is-top-right"
            class="custom-datepicker custom-width-300"
            editable
          />
        </section>
      </div>

      <div>
        <b-button
          :label="General.DOWNLOAD"
          class="mt-15 has-text-weight-semibold"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          :disabled="isLoading"
          @click="createReport()"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { App, General } from "@/utils/constants";
import { USER_PERMISSIONS as userPermissions } from "@/utils/enumerate";

import debounce from "lodash/debounce";

import ListDownloadFiles from "@/components/ListDownloadFiles";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "Reports",
  data() {
    return {
      App,
      General,
      reportType: null,
      reportSubject: null,
      subjectName: "",
      reportBookmarks: [],
      searchTag: "",
      reportCapacities: [],
      reportPart: [],
      filterCapacity: "",
      filterPart: "",
      locationConfig: Object.freeze({
        id: "pt-br",
        firstDayOfWeek: 1,
        masks: { input: `YYYY-DD-MM` }
      }),
      modelConfig: {
        start: {
          timeAdjust: "00:00:00"
        },
        end: {
          timeAdjust: "23:59:59"
        }
      },
      date: {},
      dataEndKey: Date.now(),
      datekey: uuidv4(),
      isLoading: false
    };
  },
  computed: {
    ...mapGetters({
      subjects: "getSubjects",
      getBookmarks: "getBookmarks",
      hasPermission: "hasPermission",
      currentUser: "getCurrentUser",
      capacitiesFiltered: "getCapacitiesFiltered",
      getFilteredParts: "getFilteredParts",
      getReportTypes: "getReportTypes",
      isContrast: "isContrast"
    }),

    hasPermissionAdmMaster() {
      return this.hasPermission(userPermissions["MANAGE_REPORT_ADM"]);
    },

    hasPermissionAdmPart() {
      return this.hasPermission(userPermissions["MANAGE_REPORT_PART"]);
    },

    hasPermissionAdmCapacity() {
      return this.hasPermission(userPermissions["MANAGE_REPORT_UNIT"]);
    },

    isInvalidDateStart() {
      return new Date(this.date.start) == "Invalid Date";
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.bookmarkRequest({});
    if (this.hasPermissionAdmMaster) {
      await this.capacityRequest({});
    } else if (this.hasPermissionAdmPart) {
      await this.capacityRequest({
        part_id: this.currentUser.capacity.part_id
      });
    } else if (this.hasPermissionAdmCapacity) {
      await this.capacityRequest({
        part_id: this.currentUser.capacity.part_id,
        capacity_id: this.currentUser.capacity.capacity_id
      });
    }
    await this.reportTypesRequest();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      subjectRequest: "subjectRequest",
      bookmarkRequest: "bookmarkRequest",
      capacityRequest: "capacityRequest",
      reportTypesRequest: "requestReportTypes",
      reportRequest: "reportRequest"
    }),

    ...mapMutations(["setItemDownload", "clearListDownloadFiles"]),

    openAlertMessage(message, type = "is-danger", duration = 8000) {
      this.snackToast.open({
        message: message,
        type: type,
        duration: duration
      });
    },

    setFilterPart(text) {
      this.filterPart = text;
    },

    getSubjects: debounce(async function(search_text) {
      this.isLoading = true;
      await this.subjectRequest(search_text);
      this.isLoading = false;
    }, 500),

    setSearchTag(text) {
      this.isLoading = true;
      this.searchTag = text;
      this.isLoading = false;
    },

    getTags() {
      return (
        this.getBookmarks?.filter(tag => {
          return (
            (tag.active || tag.is_global_bookmark) &&
            tag.name.toLowerCase().includes(this.searchTag.toLowerCase())
          );
        }) || []
      );
    },

    setFilterCapacity(text) {
      this.isLoading = true;
      this.filterCapacity = text;
      this.isLoading = false;
    },

    async createReport() {
      if (this.date.start && !this.date.end) {
        return this.openAlertMessage(App.ReportsScreen.INFORM_DATE);
      } else if (this.reportType) {
        const file_index = uuidv4();

        const finalData = {
          type: this.reportType.type,
          subject: this.reportSubject?.id || null,
          global_bookmarks: this.reportBookmarks?.map(bookmark => bookmark.id),
          capacities: this.reportCapacities?.map(capacity => capacity.id),
          part: this.reportPart?.map(part => part.id),
          start_date: this.date?.start || null,
          end_date: this.date?.end || null,
          file_index
        };

        await this.exportReport(finalData);
      } else {
        this.openAlertMessage(App.ReportsScreen.INFORM_TYPE);
      }
    },

    async exportReport(data) {
      this.isLoading = true;
      this.setItemDownload({
        name: App.ReportsScreen.EXPORT_REPORT,
        file_index: data.file_index,
        report: false,
        percentage: 0,
        status: "pending"
      });
      this.snackQueue.open({
        id: "download_files",
        parent: this,
        component: ListDownloadFiles,
        closeAction: this.clearListDownloadFiles
      });
      await this.reportRequest(data);
      this.isLoading = false;
    },

    resetDateEnd() {
      this.date.end = undefined;
      this.dataEndKey = Date.now();
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.wrapper-reports {
  background-color: $color-1;
  padding: 20px;

  .custom-box {
    border: 1px solid var(--dynamic-border-color);
    border-radius: 8px;
    padding: 20px;
  }

  .optional-label {
    font-size: 10px;
    font-weight: 300;
    color: #888;
  }

  .field-box {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .line-label {
    display: flex;
    flex: 1;
    margin-left: 10px;
    align-self: center;
    height: 2px;
    background-color: $color-7;
    opacity: 0.5;
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables";
.wrapper-reports {
  .custom-width-300 {
    width: 300px !important;
  }

  .custom-datepicker {
    .dropdown-item {
      background-color: $color-1 !important;
    }
    .is-today {
      border: solid 1px $color-12 !important;
    }
    .pagination .icon {
      color: $color-12 !important;
    }
  }
}
</style>
